/**
 * redactCritical — browser-side payment-card / SSN scrub.
 *
 * ============================================================================
 *  ⚠  THIS IS A MIRROR OF THE BACKEND.  ⚠
 *  The authoritative copy is `redact_critical` in `shared/pii.py`.
 *  These two MUST stay in lockstep: same patterns, same Luhn rule, same
 *  tokens ({CARD} / {SSN}). If you change one, change the other, and keep the
 *  shared test vectors passing on both sides:
 *      frontend : src/utils/redactCritical.test.ts
 *      backend  : ci-tests/backend/test_redact_critical.py
 * ============================================================================
 *
 * WHY a second copy in the browser when the backend already scrubs:
 *   The BACKEND is the guarantee — it covers every pipeline into the agent
 *   (present and future) and cannot be bypassed. This browser copy is
 *   defense-in-depth: it removes a card the instant the customer hits send —
 *   BEFORE the message leaves their device — so the raw digits never travel
 *   the network, never sit in our servers' memory, and can never be captured
 *   by any browser-side telemetry. A card typed into chat is gone before our
 *   backend ever has a chance to see it. (CPOC-140: the goal is that a typed
 *   number reaches as few systems as possible, TRACI above all.)
 *
 * Only payment cards and SSNs are touched — the two things that must never
 * reach a log or a human. Names / emails / phone numbers are deliberately left
 * intact (the agent and the rep need that context); the backend's heavier
 * at-rest scrub handles those before anything is persisted.
 */

// CPOC-140. Friendly text for the shopper's own bubble; the wire and stored token stays {CARD}.
export const CARD_TOKEN = '{CARD}';
export const CARD_FRIENDLY = '[card number removed]';
export const WITHHELD_TEXT = '[message withheld]';
export const CARD_REMOVED_NOTE =
  "We removed a card number from your message to keep it safe. Please don't type card details in chat. " +
  'You can pay at checkout on tirerack.com, or ask your rep about other ways to pay.';

const ZW = '​‌‍﻿';
// Gap between two digits of one number: 0-2 of space, tab, newline, dash, dot, slash, zero-width.
const GAP = '[ \\t\\r\\n\\-./' + ZW + ']';
const CARD_RUN_SRC = '[0-9](?:' + GAP + '{0,2}[0-9])+';
const LEN_ORDER = [16, 15, 19, 18, 17, 14, 13];
const MONTH_YEAR = '(?:0?[1-9]|1[0-2])[ ]?[/-][ ]?(?:[0-9]{4}|[0-9]{2})(?![0-9])';
const JOIN =
  '(?:[ \\t\\r\\n,;:.\\-/|()' + ZW + ']|\\b(?:exp|expiry|expires|expiration|date|cvv2?|cvc2?|csc|cid|' +
  'security[ ]code|sec[ ]code|code|and|with|is|on)\\b)*';
const EXPIRY_AFTER = new RegExp('^(?:' + JOIN + ')(' + MONTH_YEAR + ')', 'i');
const CODE_AFTER = new RegExp('^(?:' + JOIN + ')([0-9]{3,4})(?![0-9A-Za-z]|/[0-9])', 'i');
const EXPIRY_BEFORE = new RegExp('(?:^|[^0-9])(' + MONTH_YEAR + '(?:' + JOIN + '))$', 'i');
const LABELLED_SRC =
  '\\b(?:(?:exp|expiry|expires|expiration)\\.?(?:[ ]date)?[ ]?[:=#-]?[ ]?' + MONTH_YEAR + '|' +
  '(?:cvv2?|cvc2?|csc|cid|security[ ]code|sec[ ]code)[ ]?[:=#-]?[ ]?(?:is[ ])?[0-9]{3,4}(?![0-9]))';
// US SSN in the shapes people actually type: 3-2-4 digits with dashes, spaces, dots,
// or no separators at all — "123-45-6789", "123 45 6789", "123.45.6789", "123456789".
// TRACI-615: a ZIP+4 ("48601-7731") is 5-4, not 3-2-4; the lookahead keeps it from matching as
// 486|01|-7731. Mixed separators ("123 45-6789") and bare 9-digit runs still redact.
const SSN = /\b(?!\d{5}[-. ]\d{4}\b)\d{3}[-. ]?\d{2}[-. ]?\d{4}\b/g;

/** Standard Luhn checksum — the property every real payment card number satisfies. */
export function luhnOk(digits: string): boolean {
  let total = 0;
  const parity = digits.length % 2;
  for (let i = 0; i < digits.length; i++) {
    let d = digits.charCodeAt(i) - 48;
    if (i % 2 === parity) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  return total % 10 === 0;
}

const isDigit = (c: string) => c >= '0' && c <= '9';
const isAlnum = (c: string) => /[A-Za-z0-9]/.test(c);

/** Same length as `message`: full-width digits become ASCII, NBSP a space. Used only to find matches. */
function norm(message: string): string {
  let out = '';
  for (const ch of message) {
    if (ch.charCodeAt(0) < 128) out += ch;
    else if (ch === ' ') out += ' ';
    else {
      const n = ch.normalize('NFKC');
      // Same UTF-16 length as ch (an astral digit is 2 units), so offsets still fit the original.
      out += n.length === 1 && isDigit(n) ? n + ' '.repeat(ch.length - 1) : ch;
    }
  }
  return out;
}

function isCardDigits(digits: string, compact: boolean, grouped4: boolean, cardGroups = true): boolean {
  const n = digits.length;
  if (n === 16) return compact || grouped4 || (cardGroups && luhnOk(digits));
  if (!luhnOk(digits)) return false;
  if (n === 13) return digits[0] === '4';
  if (n === 14) {
    const two = digits.slice(0, 2);
    const three = digits.slice(0, 3);
    return two === '36' || two === '38' || (three >= '300' && three <= '305');
  }
  if (n === 15) return ['34', '37'].includes(digits.slice(0, 2)) || ['2131', '1800'].includes(digits.slice(0, 4));
  if (n >= 17 && n <= 19) return '456'.includes(digits[0]) || digits.slice(0, 2) === '35';
  return false;
}

function cardSpans(text: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  const run = new RegExp(CARD_RUN_SRC, 'g');
  let m: RegExpExecArray | null;
  while ((m = run.exec(text)) !== null) {
    const pos: number[] = [];
    for (let i = m.index; i < m.index + m[0].length; i++) if (isDigit(text[i])) pos.push(i);
    let k = 0;
    while (k + 13 <= pos.length) {
      let hit: [number, number, number] | null = null;
      if (!(pos[k] > 0 && isAlnum(text[pos[k] - 1]))) {
        for (const ln of LEN_ORDER) {
          if (k + ln > pos.length) continue;
          const p = pos.slice(k, k + ln);
          const last = p[ln - 1];
          if (last + 1 < text.length && isAlnum(text[last + 1])) continue;
          const digits = p.map((i) => text[i]).join('');
          const compact = last - p[0] === ln - 1;
          const breaks: number[] = [];
          for (let j = 1; j < ln; j++) if (p[j] - p[j - 1] > 1) breaks.push(j);
          const grouped4 = ln === 16 && breaks.join(',') === '4,8,12';
          const edges = [0, ...breaks, ln];
          const sizes = edges.slice(1).map((e, i) => e - edges[i]);
          const cardGroups = sizes.every((z) => z % 4 === 0) || sizes.every((z) => z === 1);
          if (isCardDigits(digits, compact, grouped4, cardGroups)) {
            hit = [p[0], last + 1, k + ln];
            break;
          }
        }
      }
      if (hit) {
        spans.push([hit[0], hit[1]]);
        k = hit[2];
      } else k += 1;
    }
  }
  return spans;
}

function redactCardText(message: string): string {
  const text = norm(message);
  let spans = cardSpans(text);
  spans = spans.map(([s0, e0]) => {
    let s = s0;
    let e = e0;
    const before = EXPIRY_BEFORE.exec(text.slice(0, s));
    if (before) s -= before[1].length;
    for (let i = 0; i < 2; i++) {
      const m = EXPIRY_AFTER.exec(text.slice(e)) || CODE_AFTER.exec(text.slice(e));
      if (!m) break;
      e += m[0].length;
    }
    return [s, e] as [number, number];
  });
  const lab = new RegExp(LABELLED_SRC, 'gi');
  let lm: RegExpExecArray | null;
  while ((lm = lab.exec(text)) !== null) spans.push([lm.index, lm.index + lm[0].length]);
  if (!spans.length) return message;
  spans.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: Array<[number, number]> = [];
  let [cs, ce] = spans[0];
  for (const [s, e] of spans.slice(1)) {
    if (s <= ce) ce = Math.max(ce, e);
    else {
      merged.push([cs, ce]);
      cs = s;
      ce = e;
    }
  }
  merged.push([cs, ce]);
  let out = '';
  let last = 0;
  for (const [s, e] of merged) {
    out += message.slice(last, s) + CARD_TOKEN;
    last = e;
  }
  return out + message.slice(last);
}

/**
 * Deterministically redact payment cards (and an expiry or security code next to them) → `{CARD}`
 * and SSNs → `{SSN}`. Dependency-free and side-effect-free; safe to call on every message.
 * Mirrors `redact_critical` in shared/pii.py; see its docstring for the rules.
 */
export function redactCritical(message: string | null | undefined): string {
  if (!message) return message ?? '';
  return redactCardText(message).replace(SSN, '{SSN}');
}

/** Text for the shopper's own bubble: the wire token {CARD} shown as plain words. */
export function friendlyCard(text: string): string {
  return text.split(CARD_TOKEN).join(CARD_FRIENDLY);
}

/** True when redaction replaced something card-like (so the shopper gets the one-line note). */
export function hasCardToken(text: string): boolean {
  return text.includes(CARD_TOKEN);
}

/**
 * Fail-closed wrapper: if redaction throws for any reason, return the withheld text instead of the
 * raw message. `failed` tells the caller to show the same note.
 */
export function safeRedact(
  message: string | null | undefined,
  fn: (m: string | null | undefined) => string = redactCritical,
): { text: string; changed: boolean; failed: boolean } {
  try {
    const text = fn(message);
    return { text, changed: text !== (message ?? ''), failed: false };
  } catch {
    return { text: WITHHELD_TEXT, changed: true, failed: true };
  }
}

// ── TRACI-888 port: a card sent a few digits at a time ─────────────────────────────────────
// Mirrors split_card_indices in shared/pii.py. The browser holds the shopper's own prior lines, so
// the piece that COMPLETES a Luhn-valid run is replaced before it leaves. Earlier pieces already
// left (known gap).
const CARD_PIECE = /^\s*([^\d]{0,40}?)(\d(?:[ -]?\d){2,15})\s*[.!]?\s*$/;
const PHONE_SHAPE = /^\d{3}[ -]\d{3}[ -]\d{4}$/;
const CARD_EXPIRY = /^\s*(?:0?[1-9]|1[0-2])\s*[/-]\s*(?:\d{2}|\d{4})\s*$/;
const CARD_TOKENS = [CARD_TOKEN, '{CREDIT_DEBIT_CARD_NUMBER}'];
const CARD_FOLLOW_UP_LINES = 4;

function cardPiece(text: string): string | null {
  const m = CARD_PIECE.exec(text || '');
  if (!m || PHONE_SHAPE.test(m[2])) return null;
  return m[2].replace(/\D/g, '');
}

export function splitCardIndices(lines: Array<string | null | undefined>): Set<number> {
  const marked = new Set<number>();
  let run: Array<[number, string]> = [];
  let followUps = 0;
  lines.forEach((line, i) => {
    const text = line || '';
    const followUp = followUps > 0;
    followUps = Math.max(0, followUps - 1);
    if (CARD_TOKENS.some((t) => text.includes(t))) {
      followUps = CARD_FOLLOW_UP_LINES;
      return;
    }
    const digits = cardPiece(text);
    if (digits !== null) {
      run.push([i, digits]);
      let total = 0;
      let card: number | null = null;
      for (let start = run.length - 1; start >= 0; start--) {
        total += run[start][1].length;
        if (total > 19) break;
        if (start < run.length - 1 && total >= 13 && luhnOk(run.slice(start).map((r) => r[1]).join(''))) card = start;
      }
      if (card !== null) {
        run.slice(card).forEach(([idx]) => marked.add(idx));
        followUps = CARD_FOLLOW_UP_LINES;
      } else if (followUp && (digits.length === 3 || digits.length === 4)) marked.add(i);
      return;
    }
    if (/\d/.test(text)) {
      run = [];
      if (followUp && CARD_EXPIRY.test(text)) marked.add(i);
    }
  });
  return marked;
}

/** `text` becomes `{CARD}` when it is the line that completes a card sent in parts after `prior`. */
export function redactCompletingPiece(prior: string[], text: string): string {
  const lines = [...prior, text];
  return splitCardIndices(lines).has(lines.length - 1) ? CARD_TOKEN : text;
}

/**
 * CPOC-140: true when `text` holds a run of 8+ digits (gaps allowed), the start of a card being
 * typed. A lookup that sends keystrokes to our servers (vehicle typeahead) must not fire on it:
 * waiting for the 13th digit would already have sent the first 12.
 */
export function hasLongDigitRun(text: string | null | undefined, min = 8): boolean {
  const t = norm(text || '');
  const run = new RegExp(CARD_RUN_SRC, 'g');
  let m: RegExpExecArray | null;
  while ((m = run.exec(t)) !== null) {
    if (m[0].replace(/[^0-9]/g, '').length >= min) return true;
  }
  return false;
}
